
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import DragDots from "./DragDots.vue";
import SpacingBars from "./SpacingBars.vue";
import ColumnDragSizer from "./ColumnDragSizer.vue";
import NodeTitlebar from "./NodeTitlebar.vue";
import NodeHoverControls from "./NodeHoverControls.vue";
import NodeContentEditable from "@/components/Canvas/OverlaidUI/NodeContentEditable.vue";
import { documentStore, uiStore } from "@/store/store-accessor";
import {
  getDomElementFromTreeNode,
  shouldHideElementNodeControls
} from "@/lib/canvas/helpers";
import { findNode } from "@/lib/tree/tree";
import { TreeNode } from "@/types/tree";

@Component({
  components: {
    NodeContentEditable,
    DragDots,
    SpacingBars,
    ColumnDragSizer,
    NodeTitlebar,
    NodeHoverControls
  }
})
export default class NodeChrome extends Vue {
  observer?: MutationObserver;
  hoverObserver?: MutationObserver;
  hideTitleBar = false;
  hideHoverControls = false;

  get element() {
    return uiStore.activeElement || false;
  }

  get hoveredElement() {
    if (uiStore.lastHoveredOnCanvasElementId) {
      return findNode(
        documentStore.document.tree,
        uiStore.lastHoveredOnCanvasElementId
      );
    }

    return false;
  }

  get hideClass() {
    let prefix = null;

    if (this.hideTitleBar && this.hideHoverControls) {
      prefix = "all";
    } else if (this.hideHoverControls) {
      prefix = "hover";
    } else if (this.hideTitleBar) {
      prefix = "titlebar";
    }

    return prefix ? `node-chrome--hide-${prefix}` : null;
  }

  @Watch("element")
  onElementSelected() {
    this.observer?.disconnect();
    if (!this.element) return;

    this.observer = this.observePositionChange(this.element, target => {
      this.hideTitleBar = shouldHideElementNodeControls(target);
    });
  }

  @Watch("hoveredElement")
  onHoveredElementSelected() {
    this.hoverObserver?.disconnect();
    if (!this.hoveredElement) return;

    this.hoverObserver = this.observePositionChange(
      this.hoveredElement,
      target => {
        this.hideHoverControls = shouldHideElementNodeControls(target);
      }
    );
  }

  beforeDestroy() {
    this.destroyObserver();
  }

  observePositionChange(
    element: TreeNode | false,
    callback: (target: HTMLElement) => void
  ) {
    if (!element) return;
    const node = getDomElementFromTreeNode(element);

    if (!node) return;

    const onMutate = (mutations: MutationRecord[]) => {
      mutations.forEach(record => {
        const target = record.target as HTMLElement;
        callback(target);
      });
    };

    const observer = new MutationObserver(onMutate);

    const options = {
      attributes: true,
      attributeFilter: ["class"]
    };

    observer.observe(node, options);
    return observer;
  }

  destroyObserver() {
    this.observer?.disconnect();
    this.hoverObserver?.disconnect();
  }
}
